import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useTranslation } from 'react-i18next';
import { HiOutlineMenu, HiX } from 'react-icons/hi';
import ContactForm from './ContactForm';
import { scrollToHash } from '../utils/scrollToHash';

export default function Header({ onContactClick }) {
  const { t, i18n } = useTranslation('header');
  const [menuOpen, setMenuOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('lng', lng);
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  const handleNavigateToAnchor = (hash) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToHash(hash), 100);
    } else {
      scrollToHash(hash);
    }
  };

  return (
    <>
      <header className="w-full fixed top-0 left-0 z-50 backdrop-blur-md transition-colors animate-fade-in bg-[#0a111f]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between h-[80px]">
          <div className="flex items-center gap-3">
            <Link to="/" className="transition-transform hover:scale-105">
              <img src={logo} alt="АскомМЕТ" className="h-12 w-auto" />
            </Link>
            <div className="font-display text-lg font-semibold bg-gradient-to-r from-[#0070d2] to-[#00c2ff] bg-clip-text text-transparent">
              {t('company')}
              <span className="block text-sm font-sans font-normal text-cyan-300">{t('tagline')}</span>
            </div>
          </div>

          <div className="md:hidden">
            {menuOpen ? (
              <HiX className="w-7 h-7 cursor-pointer text-white" onClick={() => setMenuOpen(false)} />
            ) : (
              <HiOutlineMenu className="w-7 h-7 cursor-pointer text-white" onClick={() => setMenuOpen(true)} />
            )}
          </div>

          <nav className="hidden md:flex gap-8 font-medium items-center text-gray-200">
            <Link
              to="/about"
              className="relative transition hover:text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff]"
            >
              {t('about')}
            </Link>

            <div className="relative group">
              <span
                className="cursor-pointer relative px-2 py-1 inline-block transition hover:text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff]"
                onClick={() => handleNavigateToAnchor('#services')}
              >
                {t('services')}
              </span>
              <div className="absolute top-full left-0 mt-2 shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[260px] border divide-y bg-[#0d1524] border-white/10 divide-white/10">
                {[
                  ['/video', 'video'],
                  ['/localsystems', 'localsystems'],
                  ['/radio', 'radio'],
                  ['/antiterror', 'antiterror'],
                  ['/digitalforensics', 'digitalforensics'],
                  ['/mesh', 'mesh'],
                  ['/drones', 'drones'],
                  ['/antidrone', 'antidrone'],
                  ['/sts', 'sts'],
                ].map(([to, key]) => (
                  <Link
                    key={to}
                    to={to}
                    className="block px-5 py-3 transition text-gray-200 hover:bg-white/5 hover:text-cyan-300"
                  >
                    {t(key)}
                  </Link>
                ))}
              </div>
            </div>

            <span
              onClick={() => handleNavigateToAnchor('#filial')}
              className="cursor-pointer relative transition hover:text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff]"
            >
              {t('filial')}
            </span>
            <span
              onClick={() => handleNavigateToAnchor('#partners')}
              className="cursor-pointer relative transition hover:text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff]"
            >
              {t('partners')}
            </span>
            <span
              onClick={() => handleNavigateToAnchor('#contacts')}
              className="cursor-pointer relative transition hover:text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff]"
            >
              {t('contacts')}
            </span>

            <button
              onClick={onContactClick}
              className="font-display btn-primary btn-sm"
            >
              {t('contact')}
            </button>

            <div className="flex gap-2 ml-2">
              {['ru', 'en', 'kz'].map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={`text-sm px-2 ${
                    i18n.language === lng
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#0070d2] to-[#00c2ff] font-bold'
                      : 'hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#0070d2] hover:to-[#00c2ff] text-gray-400'
                  }`}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Мобильное меню */}
        <div
          className={`absolute top-[80px] left-0 w-full z-50 shadow-md rounded-b-lg mobile-menu-transition bg-[#0a111f] ${
            menuOpen ? 'mobile-menu-open' : ''
          }`}
        >
          <ul className="divide-y divide-white/10">
            {[
              ['/about', 'about'],
              null,
              ['/video', 'video'],
              ['/localsystems', 'localsystems'],
              ['/antiterror', 'antiterror'],
              ['/digitalforensics', 'digitalforensics'],
              ['/mesh', 'mesh'],
              ['/drones', 'drones'],
              ['/antidrone', 'antidrone'],
              ['/sts', 'sts'],
              ['/radio', 'radio'],
            ].map((entry, i) => {
              const itemClass = 'px-6 py-4 font-medium text-gray-200 hover:text-cyan-300';
              if (entry === null) {
                return (
                  <li key="services" className={itemClass}>
                    <span onClick={() => { setMenuOpen(false); handleNavigateToAnchor('#services'); }}>{t('services')}</span>
                  </li>
                );
              }
              const [to, key] = entry;
              return (
                <li key={to} className={itemClass}>
                  <Link to={to} onClick={() => setMenuOpen(false)}>{t(key)}</Link>
                </li>
              );
            })}

            <li className="px-6 py-4">
              <button
                className="font-display btn-primary w-full justify-center"
                onClick={() => setFormOpen(true)}
              >
                {t('contact')}
              </button>
            </li>
            <li className="px-6 py-4 flex justify-center gap-4">
              {['ru', 'en', 'kz'].map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={`text-sm ${
                    i18n.language === lng ? 'text-cyan-300 font-bold' : 'text-gray-400 hover:text-cyan-300'
                  }`}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </li>
          </ul>
        </div>
      </header>

      {formOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="glass-card relative w-full max-w-lg rounded-3xl p-8 bg-[#0a111f]/95">
            <button
              onClick={() => setFormOpen(false)}
              className="absolute top-4 right-5 text-white/50 hover:text-cyan-300 text-2xl font-bold transition"
            >
              &times;
            </button>
            <h2 className="font-display text-xl font-bold text-white mb-6">{t('contactUs')}</h2>
            <ContactForm />
          </div>
        </div>
      )}
    </>
  );
}
